import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE;

// Fields the backend self-update expects (must send all to avoid wiping data)
const ALL_FIELDS = [
    "first_name", "middle_name", "last_name", "gender", "dob", "ethnicity", "citizenship", "marital_status",
    "education_qualification_code", "address1", "address2", "pin_code", "email", "mobile_no", "whatsapp_no",
    "initiated_name", "photo", "spiritual_master_id", "first_initiation_date", "iskcon_first_contact_date",
    "second_initiated", "second_initiation_date", "full_time_devotee", "temple_name"
];

export default function CompleteProfileModal({ profile, onComplete, onClose }) {
    const [gender, setGender] = useState(profile?.gender || "");
    const [templeName, setTempleName] = useState(profile?.temple_name || "");
    const [photoFile, setPhotoFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const needGender = !profile?.gender || profile.gender.toString().trim() === "";
    const needTemple = !profile?.temple_name || profile.temple_name.toString().trim() === "";
    const needPhoto = !profile?.photo || profile.photo.toString().trim() === "";

    const handleSave = async () => {
        // Basic validation for the fields we are asking for
        if (needGender && !gender) return setError("Please select your gender.");
        if (needTemple && !templeName.trim()) return setError("Please enter your temple name.");
        if (needPhoto && !photoFile) return setError("Please upload your profile photo.");

        setSaving(true);
        setError("");
        try {
            const token = localStorage.getItem("token");
            const email = sessionStorage.getItem("email") || profile?.email || localStorage.getItem("userId");
            const formData = new FormData();
            ALL_FIELDS.forEach((f) => {
                if (f === "photo") return; // handled separately
                if (f === "gender") formData.append("gender", gender || profile?.gender || "");
                else if (f === "temple_name") formData.append("temple_name", templeName || profile?.temple_name || "");
                else formData.append(f, profile?.[f] ?? "");
            });
            if (photoFile) {
                formData.append("photo", photoFile);
            } else if (profile?.photo) {
                formData.append("photo", profile.photo);
            }

            await axios.put(`${API_BASE}/api/devotees/${email}/self`, formData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSaving(false);
            if (onComplete) onComplete();
        } catch (err) {
            setSaving(false);
            setError("Failed to save. " + (err.response?.data?.error || err.message));
        }
    };

    return (
        <Modal show centered backdrop="static" keyboard={false} onHide={onClose || (() => {})}>
            <Modal.Header closeButton={!!onClose}>
                <Modal.Title style={{ color: "#3d5a1a", fontWeight: 700, fontSize: "1.2rem" }}>
                    <i className="bi bi-person-badge me-2"></i>Complete Your Profile
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted" style={{ fontSize: "0.95rem" }}>
                    Please fill in the details below to complete your profile.
                </p>

                {needGender && (
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Gender</label>
                        <select
                            className="form-select"
                            value={gender}
                            onChange={(e) => setGender(e.target.value)}
                        >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>
                )}

                {needTemple && (
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Temple Name</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Enter your temple name"
                            value={templeName}
                            onChange={(e) => setTempleName(e.target.value)}
                        />
                    </div>
                )}

                {needPhoto && (
                    <div className="mb-2">
                        <label className="form-label fw-semibold">Profile Photo</label>
                        <input
                            type="file"
                            accept="image/*"
                            className="form-control"
                            onChange={(e) => setPhotoFile(e.target.files[0] || null)}
                        />
                        {photoFile && (
                            <div className="mt-2 text-center">
                                <img
                                    src={URL.createObjectURL(photoFile)}
                                    alt="Preview"
                                    className="rounded-circle border"
                                    style={{ width: 90, height: 90, objectFit: "cover" }}
                                />
                            </div>
                        )}
                    </div>
                )}

                {error && <div className="alert alert-danger py-2 mt-2 mb-0">{error}</div>}
            </Modal.Body>
            <Modal.Footer>
                {onClose && (
                    <Button variant="outline-secondary" onClick={onClose} disabled={saving}>
                        Later
                    </Button>
                )}
                <Button variant="success" onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : "Save & Continue"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
